import React, { useEffect, useMemo, useState } from "react";
import {
  FaChevronDown,
  FaChevronUp,
  FaExclamationTriangle,
  FaLock,
} from "react-icons/fa";
import { fetchCommissionReconciliation } from "../commissionsApi";
import { formatCurrency } from "./commissionDetailConstants";

function Figure({ label, value, currency, hint, emphasis = false }) {
  return (
    <div
      className={`rounded-lg border px-3 py-2.5 ${
        emphasis
          ? "border-orange-200 bg-orange-50"
          : "border-slate-200 bg-slate-50/70"
      }`}
    >
      <p className="mb-0 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
        {label}
      </p>
      <p
        className={`mb-0 mt-0.5 text-[15px] font-bold tabular-nums ${
          emphasis ? "text-orange-800" : "text-slate-900"
        }`}
      >
        {formatCurrency(value, currency)}
      </p>
      {hint ? (
        <p className="mb-0 mt-0.5 text-[11px] leading-snug text-slate-500">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

/**
 * Read-only recap for a student flagged for commission review (spec 10.5).
 *
 * This panel never posts anything. It gathers the figures a human needs in one
 * place so they can decide what to adjust through the normal commission and
 * finance screens.
 */
function CommissionReconciliationPanel({
  applicationId,
  applicationType = "education",
  currency = "AUD",
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    if (!applicationId) {
      setData(null);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);

    fetchCommissionReconciliation({ applicationId, applicationType })
      .then((result) => {
        if (!cancelled) setData(result);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [applicationId, applicationType]);

  const workflow = data?.workflow;
  const financials = data?.financials;
  const suggestion = data?.suggested_reversal;

  const heading = useMemo(() => {
    if (workflow?.refund_status === "Paid") return "Refund paid — reconciliation required";
    if (workflow?.refund_status === "Applied") return "Refund applied — commission on hold";
    if (workflow?.commission_workflow_status) {
      return `${workflow.commission_workflow_status} — please review`;
    }
    return "Commission review required";
  }, [workflow]);

  // Only appears when the automation has actually flagged this student.
  if (loading || !data?.needs_attention) return null;

  return (
    <div className="overflow-hidden rounded-xl border border-orange-200 bg-white shadow-sm">
      <button
        type="button"
        onClick={() => setExpanded((prev) => !prev)}
        className="flex w-full items-center gap-2.5 border-b border-orange-100 bg-orange-50/80 px-3.5 py-2.5 text-left"
        style={{ boxShadow: "none", outline: "none" }}
      >
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-orange-100 text-orange-700">
          <FaExclamationTriangle size={13} />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[13.5px] font-bold text-orange-900">
            {heading}
          </span>
          <span className="block truncate text-[11.5px] text-orange-700">
            {workflow?.student_outcome
              ? `Student outcome: ${workflow.student_outcome}`
              : "Triggered by an application status change"}
            {workflow?.last_application_status_label
              ? ` · ${workflow.last_application_status_label}`
              : ""}
          </span>
        </span>
        <span className="inline-flex items-center gap-1 rounded-full bg-white px-2 py-0.5 text-[10.5px] font-semibold text-orange-700 ring-1 ring-orange-200">
          <FaLock size={9} />
          Read only
        </span>
        {expanded ? (
          <FaChevronUp size={12} className="text-orange-700" />
        ) : (
          <FaChevronDown size={12} className="text-orange-700" />
        )}
      </button>

      {expanded ? (
        <div className="p-3.5">
          <div className="grid grid-cols-2 gap-2.5 lg:grid-cols-3">
            <Figure
              label="Tuition paid by student"
              value={financials?.tuition_paid}
              currency={currency}
            />
            <Figure
              label="Commission invoiced"
              value={financials?.commission_claimed}
              currency={currency}
              hint="Raised against the provider"
            />
            <Figure
              label="Commission received"
              value={financials?.commission_received}
              currency={currency}
              hint="Already banked"
            />
            <Figure
              label="Bonus received"
              value={financials?.bonus_received}
              currency={currency}
            />
            <Figure
              label="Commission expected"
              value={financials?.commission_expected}
              currency={currency}
              hint={
                financials?.total_installments
                  ? `${financials.paid_installments}/${financials.total_installments} instalments paid`
                  : undefined
              }
            />
            <Figure
              label="Suggested reversal"
              value={suggestion?.potential_reversal}
              currency={currency}
              hint="Suggestion only"
              emphasis
            />
          </div>

          {suggestion?.invoiced_not_received > 0 ? (
            <p className="mb-0 mt-2.5 rounded-md bg-amber-50 px-3 py-2 text-[12px] leading-snug text-amber-900 ring-1 ring-amber-200">
              A further{" "}
              <strong className="tabular-nums">
                {formatCurrency(suggestion.invoiced_not_received, currency)}
              </strong>{" "}
              has been invoiced but not received. That normally needs cancelling
              or crediting rather than reversing.
            </p>
          ) : null}

          <p className="mb-0 mt-2.5 text-[12px] leading-snug text-slate-600">
            {suggestion?.reason} Nothing on this panel has been applied — make any
            adjustment through the commission instalments and finance screens as
            usual.
          </p>

          {data?.recent_changes?.length ? (
            <div className="mt-3 border-t border-slate-100 pt-2.5">
              <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                What the system changed
              </p>
              <ul className="mb-0 list-none space-y-1 pl-0">
                {data.recent_changes.slice(0, 6).map((change) => (
                  <li key={change.id} className="text-[12px] text-slate-600">
                    <span className="font-semibold text-slate-800">
                      {String(change.field_changed).replace(/_/g, " ")}
                    </span>
                    {": "}
                    {change.old_value || "—"} → {change.new_value || "—"}
                    {change.trigger_status_title
                      ? ` (${change.trigger_status_title})`
                      : ""}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export default CommissionReconciliationPanel;
